import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { api } from '@/lib/api';
import { formatDateTime } from '@/lib/utils';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import Modal from '@/components/ui/Modal';
import {
  Search,
  Download,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Ban,
  Send,
  Eye,
  RefreshCw,
  Loader2,
  ChevronDown,
  ChevronUp,
  Calendar,
  User,
  Info,
} from 'lucide-react';

const STATUS_CONFIG: Record<string, { label: string; className: string; icon: React.ElementType }> = {
  ready: { label: 'Pendiente', className: 'bg-yellow-100 text-yellow-700', icon: Clock },
  signed: { label: 'Firmado', className: 'bg-green-100 text-green-700', icon: CheckCircle },
  declined: { label: 'Rechazado', className: 'bg-red-100 text-red-700', icon: XCircle },
  expired: { label: 'Expirado', className: 'bg-gray-100 text-gray-500', icon: AlertCircle },
  cancelled: { label: 'Cancelado', className: 'bg-gray-100 text-gray-400', icon: Ban },
  started: { label: 'Iniciado', className: 'bg-blue-100 text-blue-700', icon: Clock },
  delivered: { label: 'Entregado', className: 'bg-sereno-blue text-sereno-dark', icon: Send },
};

function StatusBadge({ status }: { status: string }) {
  const config = STATUS_CONFIG[status] || { label: status, className: 'bg-gray-100 text-gray-600', icon: FileText };
  const Icon = config.icon;
  return (
    <span className={`badge inline-flex items-center gap-1 ${config.className}`}>
      <Icon size={12} />
      {config.label}
    </span>
  );
}

// Click & Sign devuelve los timestamps en segundos y como string ("1786524164"),
// así que hay que convertir antes de decidir si son segundos o milisegundos.
function formatTimestamp(ts: number | string | undefined): string {
  if (ts === undefined || ts === null || ts === '') return '-';
  const n = Number(ts);
  if (!Number.isFinite(n) || n <= 0) return String(ts);
  const ms = n < 1e12 ? n * 1000 : n;
  return formatDateTime(new Date(ms).toISOString());
}

// Los flags booleanos de Click & Sign llegan como 'Y' / 'N'
function isYes(value: unknown): boolean {
  return value === 'Y' || value === 'true' || value === true;
}

// Sólo hay PDF que enseñar cuando la firma se ha completado o ya hay evidencia
function hasDocument(sig: any): boolean {
  return sig.signature_status === 'signed' || isYes(sig.signature_evidence_generated);
}

function toDateString(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function defaultStartDate(): string {
  const d = new Date();
  d.setDate(d.getDate() - 14);
  return toDateString(d);
}

export default function ContractsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(() => toDateString(new Date()));
  const [expanded, setExpanded] = useState<string | null>(null);
  const [detailModal, setDetailModal] = useState<any>(null);
  const [loadingDetail, setLoadingDetail] = useState<string | null>(null);
  const [reminderModal, setReminderModal] = useState<{ signatureId: string; signatories: any[] } | null>(null);
  const [reminderChannel, setReminderChannel] = useState<'email' | 'sms'>('email');
  const [reminderSignatory, setReminderSignatory] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [pdfViewer, setPdfViewer] = useState<{ signatureId: string; filename: string } | null>(null);

  // Contract-Owner links (read-only display)
  const [contractLinks, setContractLinks] = useState<Record<string, { ownerId: string; ownerName: string }>>({});

  useEffect(() => {
    api.getContractLinks().then(setContractLinks).catch(() => {});
  }, []);

  const params: Record<string, string> = {};
  if (search) params.search = search;
  if (statusFilter) params.signature_status = statusFilter;
  if (startDate) params.start_date = String(Math.floor(new Date(startDate).getTime() / 1000));
  if (endDate) params.end_date = String(Math.floor(new Date(endDate + 'T23:59:59').getTime() / 1000));

  const { data: signatures, loading, error, refresh } = useApi<any[]>(
    () => api.getSignatures(params),
    [search, statusFilter, startDate, endDate]
  );

  // El visor apunta directamente al proxy del backend: no hace falta pedir antes
  // los metadatos del documento (esa llamada arrastraba el PDF en base64).
  const openViewer = (sig: any) => {
    setPdfViewer({
      signatureId: String(sig.signature_id),
      filename: `${sig.contract_id || `contrato-${sig.signature_id}`}.pdf`,
    });
  };

  const openSignatoryDetail = async (signatureId: string, signatoryId: string) => {
    setLoadingDetail(signatoryId);
    try {
      setDetailModal(await api.getSignatoryDetails(signatureId, signatoryId));
    } catch (err) {
      console.error('Signatory detail error:', err);
      alert('Error al obtener el detalle del firmante');
    } finally {
      setLoadingDetail(null);
    }
  };

  const handleSendReminder = async () => {
    if (!reminderModal || !reminderSignatory) return;
    setSending(true);
    try {
      await api.sendSignatureReminder(reminderModal.signatureId, {
        signatory_id: reminderSignatory,
        channel: reminderChannel,
      });
      alert('Recordatorio enviado correctamente');
      setReminderModal(null);
    } catch (err) {
      console.error('Reminder error:', err);
      alert('Error al enviar el recordatorio');
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-3">
        <h2 className="text-lg font-semibold">Firmas en Click &amp; Sign</h2>
        <button onClick={refresh} className="btn-secondary flex items-center gap-2 text-sm sm:text-base">
          <RefreshCw size={16} />
          <span className="hidden sm:inline">Actualizar</span>
        </button>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-sereno-dark/30" />
            <input
              type="text"
              placeholder="Buscar por contrato, nombre, email, DNI..."
              className="input pl-9"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <select
            className="input w-auto"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="">Todos los estados</option>
            <option value="ready">Pendiente</option>
            <option value="started">Iniciado</option>
            <option value="delivered">Entregado</option>
            <option value="signed">Firmado</option>
            <option value="declined">Rechazado</option>
            <option value="expired">Expirado</option>
            <option value="cancelled">Cancelado</option>
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-gray-100">
          <Calendar size={16} className="text-sereno-dark/40 flex-shrink-0" />
          <span className="text-sm text-sereno-dark/50 flex-shrink-0">Desde</span>
          <input
            type="date"
            className="input w-auto text-sm"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
          />
          <span className="text-sm text-sereno-dark/50 flex-shrink-0">Hasta</span>
          <input
            type="date"
            className="input w-auto text-sm"
            value={endDate}
            max={toDateString(new Date())}
            onChange={e => setEndDate(e.target.value)}
          />
        </div>
      </div>

      {/* Results */}
      {error ? (
        <div className="card border border-red-200 bg-red-50 p-4 flex items-start gap-3">
          <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-red-700">Error al cargar contratos</p>
            <p className="text-sm text-red-600 mt-1">{error}</p>
            <button onClick={refresh} className="btn-secondary text-sm mt-3 flex items-center gap-2">
              <RefreshCw size={14} />
              Reintentar
            </button>
          </div>
        </div>
      ) : loading ? (
        <LoadingSpinner />
      ) : !signatures || signatures.length === 0 ? (
        <EmptyState
          title="No hay contratos"
          description="Los contratos enviados a través de Click & Sign aparecerán aquí"
        />
      ) : (
        <div className="space-y-3">
          {signatures.map((sig: any) => (
            <div key={sig.signature_id} className="card p-0 overflow-hidden">
              {/* Contract row header */}
              <div
                className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 px-4 sm:px-6 py-3 sm:py-4 cursor-pointer hover:bg-sereno-blue/20 transition-colors"
                onClick={() => setExpanded(expanded === String(sig.signature_id) ? null : String(sig.signature_id))}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <FileText size={18} className="text-sereno-dark/40 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">
                      {sig.contract_id || `#${sig.signature_id}`}
                    </p>
                    <div className="flex items-center gap-2">
                      <p className="text-xs text-sereno-dark/40 truncate">
                        {sig.config_name || 'Sin configuración'} · ID: {sig.signature_id}
                      </p>
                      {contractLinks[String(sig.signature_id)] && (
                        <button
                          onClick={e => { e.stopPropagation(); navigate(`/owners/${contractLinks[String(sig.signature_id)].ownerId}`); }}
                          className="badge bg-sereno-blue text-sereno-dark text-[10px] inline-flex items-center gap-1 hover:bg-sereno-blue/80 transition-colors"
                          title="Ver propietario"
                        >
                          <User size={10} />
                          {contractLinks[String(sig.signature_id)].ownerName}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="ml-auto flex items-center gap-2 sm:hidden flex-shrink-0">
                    <StatusBadge status={sig.signature_status} />
                    {expanded === String(sig.signature_id) ? <ChevronUp size={16} className="text-sereno-dark/30" /> : <ChevronDown size={16} className="text-sereno-dark/30" />}
                  </div>
                </div>

                <div className="flex items-center gap-2 sm:gap-3 pl-8 sm:pl-0 text-xs">
                  <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
                    {sig.signatories?.map((st: any) => (
                      <span key={st.signatory_id} className="text-sereno-dark/60">
                        {st.name} {st.surname}
                      </span>
                    ))}
                  </div>

                  <div className="hidden sm:block flex-shrink-0">
                    <StatusBadge status={sig.signature_status} />
                  </div>

                  <span className="text-sereno-dark/40 flex-shrink-0">
                    {formatTimestamp(sig.signature_start_date)}
                  </span>

                  <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 ml-auto sm:ml-0">
                    {hasDocument(sig) && (
                      <a
                        href={api.signaturePdfUrl(String(sig.signature_id), { download: true })}
                        onClick={e => e.stopPropagation()}
                        className="p-1.5 rounded hover:bg-sereno-blue/40 text-sereno-dark/50 hover:text-sereno-dark transition-colors"
                        title="Descargar documento firmado"
                      >
                        <Download size={16} />
                      </a>
                    )}
                    {(sig.signature_status === 'ready' || sig.signature_status === 'delivered' || sig.signature_status === 'started') && (
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          setReminderModal({ signatureId: String(sig.signature_id), signatories: sig.signatories || [] });
                          setReminderSignatory(sig.signatories?.[0]?.signatory_id ? String(sig.signatories[0].signatory_id) : null);
                        }}
                        className="p-1.5 rounded hover:bg-sereno-blue/40 text-sereno-dark/50 hover:text-sereno-dark transition-colors"
                        title="Enviar recordatorio"
                      >
                        <Send size={16} />
                      </button>
                    )}
                    <span className="hidden sm:inline">
                      {expanded === String(sig.signature_id) ? <ChevronUp size={16} className="text-sereno-dark/30" /> : <ChevronDown size={16} className="text-sereno-dark/30" />}
                    </span>
                  </div>
                </div>
              </div>

              {/* Expanded signatory details */}
              {expanded === String(sig.signature_id) && sig.signatories && (
                <div className="border-t border-gray-100 bg-gray-50/50">
                  {/* Desktop table */}
                  <table className="w-full hidden md:table">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="text-left text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider px-6 py-2">Firmante</th>
                        <th className="text-left text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider px-6 py-2">Email</th>
                        <th className="text-left text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider px-6 py-2">DNI</th>
                        <th className="text-left text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider px-6 py-2">Método</th>
                        <th className="text-left text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider px-6 py-2">Estado</th>
                        <th className="text-left text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider px-6 py-2">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sig.signatories.map((st: any) => (
                        <tr key={st.signatory_id} className="border-b border-gray-50">
                          <td className="px-6 py-3 text-sm">
                            {st.name} {st.surname}
                          </td>
                          <td className="px-6 py-3 text-sm text-sereno-dark/60">
                            {st.email || '-'}
                          </td>
                          <td className="px-6 py-3 text-sm text-sereno-dark/60">
                            {st.id_number || '-'}
                          </td>
                          <td className="px-6 py-3 text-sm text-sereno-dark/60">
                            {st.signature_method?.join(', ') || '-'}
                          </td>
                          <td className="px-6 py-3">
                            <StatusBadge status={st.signatory_status} />
                          </td>
                          <td className="px-6 py-3 flex items-center gap-3">
                            {hasDocument(sig) && (
                              <button
                                onClick={() => openViewer(sig)}
                                className="text-sereno-dark/40 hover:text-sereno-dark transition-colors"
                                title="Ver documento"
                              >
                                <Eye size={16} />
                              </button>
                            )}
                            <button
                              onClick={() => openSignatoryDetail(String(sig.signature_id), String(st.signatory_id))}
                              disabled={loadingDetail === String(st.signatory_id)}
                              className="text-sereno-dark/40 hover:text-sereno-dark transition-colors"
                              title="Detalle del firmante"
                            >
                              {loadingDetail === String(st.signatory_id)
                                ? <Loader2 size={16} className="animate-spin" />
                                : <Info size={16} />}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {/* Mobile cards */}
                  <div className="md:hidden divide-y divide-gray-100">
                    {sig.signatories.map((st: any) => (
                      <div key={st.signatory_id} className="px-4 py-3 space-y-1">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium">{st.name} {st.surname}</p>
                          <div className="flex items-center gap-2">
                            <StatusBadge status={st.signatory_status} />
                            {hasDocument(sig) && (
                              <button
                                onClick={() => openViewer(sig)}
                                className="text-sereno-dark/40 hover:text-sereno-dark transition-colors p-1"
                                title="Ver documento"
                              >
                                <Eye size={16} />
                              </button>
                            )}
                            <button
                              onClick={() => openSignatoryDetail(String(sig.signature_id), String(st.signatory_id))}
                              disabled={loadingDetail === String(st.signatory_id)}
                              className="text-sereno-dark/40 hover:text-sereno-dark transition-colors p-1"
                              title="Detalle del firmante"
                            >
                              {loadingDetail === String(st.signatory_id)
                                ? <Loader2 size={16} className="animate-spin" />
                                : <Info size={16} />}
                            </button>
                          </div>
                        </div>
                        <p className="text-xs text-sereno-dark/50">{st.email || '-'}</p>
                        <div className="flex items-center gap-3 text-xs text-sereno-dark/40">
                          <span>DNI: {st.id_number || '-'}</span>
                          <span>{st.signature_method?.join(', ') || '-'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Signatory detail modal */}
      <Modal isOpen={!!detailModal} onClose={() => setDetailModal(null)} title="Detalle del firmante">
        {detailModal && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-sereno-dark/40">Nombre</span>
                <p className="font-medium">{detailModal.signatory_name} {detailModal.signatory_surname}</p>
              </div>
              <div>
                <span className="text-sereno-dark/40">Email</span>
                <p className="font-medium">{detailModal.signatory_email || '-'}</p>
              </div>
              <div>
                <span className="text-sereno-dark/40">Teléfono</span>
                <p className="font-medium">{detailModal.signatory_phone || '-'}</p>
              </div>
              <div>
                <span className="text-sereno-dark/40">DNI</span>
                <p className="font-medium">{detailModal.signatory_id_number || '-'}</p>
              </div>
              <div>
                <span className="text-sereno-dark/40">Estado</span>
                <p><StatusBadge status={detailModal.signatory_status} /></p>
              </div>
              <div>
                <span className="text-sereno-dark/40">IP</span>
                <p className="font-medium">{detailModal.ip || '-'}</p>
              </div>
              <div>
                <span className="text-sereno-dark/40">Fecha estado</span>
                <p className="font-medium">{formatTimestamp(detailModal.signatory_status_date)}</p>
              </div>
              <div>
                <span className="text-sereno-dark/40">Fecha landing</span>
                <p className="font-medium">{formatTimestamp(detailModal.signatory_landing_date)}</p>
              </div>
              <div>
                <span className="text-sereno-dark/40">Evidencia generada</span>
                <p className="font-medium">{isYes(detailModal.signatory_evidence_generated) ? 'Sí' : 'No'}</p>
              </div>
              <div>
                <span className="text-sereno-dark/40">Intentos OTP</span>
                <p className="font-medium">{detailModal.otp_tries ?? '-'}</p>
              </div>
            </div>
            {detailModal.decline_reason && (
              <div className="mt-3 p-3 bg-red-50 rounded-lg">
                <span className="text-xs font-semibold text-red-600">Motivo de rechazo</span>
                <p className="text-sm text-red-700 mt-1">{detailModal.decline_reason}</p>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Reminder modal */}
      <Modal isOpen={!!reminderModal} onClose={() => setReminderModal(null)} title="Enviar recordatorio">
        {reminderModal && (
          <div className="space-y-4">
            <div>
              <label className="label">Firmante</label>
              <select
                className="input"
                value={reminderSignatory ?? ''}
                onChange={e => setReminderSignatory(e.target.value)}
              >
                {reminderModal.signatories.map((st: any) => (
                  <option key={st.signatory_id} value={String(st.signatory_id)}>
                    {st.name} {st.surname} ({st.email})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Canal</label>
              <div className="flex gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="channel"
                    value="email"
                    checked={reminderChannel === 'email'}
                    onChange={() => setReminderChannel('email')}
                  />
                  <span className="text-sm">Email</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="channel"
                    value="sms"
                    checked={reminderChannel === 'sms'}
                    onChange={() => setReminderChannel('sms')}
                  />
                  <span className="text-sm">SMS</span>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button className="btn-secondary" onClick={() => setReminderModal(null)}>Cancelar</button>
              <button
                className="btn-primary flex items-center gap-2"
                onClick={handleSendReminder}
                disabled={sending || !reminderSignatory}
              >
                {sending ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />}
                Enviar
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* PDF Viewer modal */}
      {pdfViewer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setPdfViewer(null)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-5xl h-[90vh] mx-4 flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-100 flex-shrink-0">
              <h2 className="text-lg font-semibold text-sereno-dark truncate">{pdfViewer.filename}</h2>
              <div className="flex items-center gap-2 flex-shrink-0">
                <a
                  href={api.signaturePdfUrl(pdfViewer.signatureId, { download: true })}
                  className="btn-secondary text-sm flex items-center gap-1"
                >
                  <Download size={14} /> Descargar
                </a>
                <button onClick={() => setPdfViewer(null)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                  <XCircle size={20} />
                </button>
              </div>
            </div>
            <div className="flex-1 bg-gray-100">
              <iframe
                src={api.signaturePdfUrl(pdfViewer.signatureId)}
                className="w-full h-full border-0"
                title="PDF Viewer"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
